import classNames from "classnames";
import { useFeature } from "flagged";
import * as React from "react";
import { SetStateAction } from "react";
import { Menu } from "react-aria-menubutton";
import { FileDrop } from "react-file-drop";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";
import { Column } from "react-table";
import { toast } from "react-toastify";

import ManageWorkflowTemplateWrapper from "./manage-workflow-template-dialog/ManageWorkflowTemplateWrapper";
import style from "./workflow-templates-table.scss";
import WorkflowTemplateKebabMenu from "./WorkflowTemplateKebabMenu";
import AlertDialog from "components/alert-dialog/AlertDialog";
import ToggleablePanel from "components/header/ToggleablePanel";
import Checkmark from "components/icons/Checkmark";
import { CustomToastCloseButton } from "components/icons/CustomToastCloseButton";
import EmptyStateIcon from "components/icons/EmptyState";
import ScheduledIcon from "components/icons/ScheduledIcon";
import { SuccessIcon } from "components/icons/SuccessIcon";
import KebabMenu from "components/kebab-menu/KebabMenu";
import layout from "components/layout/layout.scss";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import MenuItemButton from "components/menu-item-button/MenuItemButton";
import Modal from "components/modal/Modal";
import SearchView from "components/search/SearchView";
import StatusBadge, { Status } from "components/status-badge/StatusBadge";
import DateCell from "components/table/DateCell";
import Table, { deriveColumnWidth } from "components/table/Table";
import TextWithTooltip from "components/table/TextWithTooltip";
import Tooltip from "components/tooltip/Tooltip";
import { TextBlock } from "components/typography/textBlock/TextBlock";
import workflowStyle from "components/workflows/all-workflows-table.scss";
import ConnectedWorkflowsList from "components/workflows/ConnectedWorkflowsList";
import ImportWorkflowsDialog from "components/workflows/ImportWorkflowsDialog";
import { determineEditorGenerationByProfileAndVersion } from "components/workflows/manage-workflow-dialog/ManageWorkflowDialog";
import ProductNameColumn from "components/workflows/ProductNameColumn";
import SidePanel from "components/workflows/SidePanel";
import WorkflowEditorV3View, {
    type VersionRestoreParams,
} from "components/workflows/workflow-editor-v3/WorkflowEditorV3View";
import WorkflowEventDatePicker from "components/workflows/WorkflowEventDatePicker";
import {
    compareEditorVersionDtos,
    deduceLatestEditorVersions,
    determineEditorToOpen,
    isEditorV3Supported,
    isNewerEditorAvailable,
    mergeAndSortEditorVersions,
    trackFetchLoaded,
} from "components/workflows/WorkflowUtil";
import {
    AUTH_WORKFLOW_TEMPLATE_DELETE,
    AUTH_WORKFLOW_TEMPLATE_EDIT,
    AUTH_WORKFLOW_TEMPLATE_MANAGEMENT_VIEW,
    AUTH_WORKFLOW_TEMPLATE_PUBLISH,
    AUTH_WORKFLOW_TEMPLATE_SECURED,
} from "domain/authority";
import {
    BDE_WORKFLOW_EDITOR_VERSION_7_12_0,
    BDE_WORKFLOW_EDITOR_VERSION_7_13_0,
    BMDE_WORKFLOW_EDITOR_VERSION_5_4_0,
    BMDE_WORKFLOW_EDITOR_VERSION_5_5_0,
} from "domain/globalConstants";
import { FeatureLicenseType } from "domain/users";
import { FilterState, WorkflowEntityType } from "domain/workflows";
import { TemplateTableData } from "domain/workflowTemplates";
import { FLAG_WORKFLOW_EDITOR_V3 } from "services/feature/FeatureFlagService";
import { Action, Category, Label, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { userService } from "services/user/users/UserService";
import { userSessionService } from "services/user/UserSessionService";
import { PublishStatus, templateService } from "services/workflows/TemplateService";
import {
    ManifestWorkflowEditor,
    Profile,
    profileToFeatureLicenseMap,
    profileToNameMap,
    WorkflowEditorV3SupportedEntry,
    workflowService,
} from "services/workflows/WorkflowService";
import {
    clearWorkflowTemplateRestoreVersionDetails,
    getWorkflowTemplateRestoreVersionDetails,
} from "services/workflows/workflowTemplateRestoreVersionRepository";
import { StoreState } from "store";
import { setUser } from "store/user";
import buttonStyle from "styles/buttons.scss";
import formStyle from "styles/form.scss";
import layoutStyle from "styles/layout.scss";
import statusStyle from "styles/status.scss";
import { RepositoryKey } from "utils/repository";
import { buildScheduleSummary, DayOfWeek } from "utils/scheduleUtils";
import { useTheme } from "utils/useTheme";

import assetGradingIcon from "assets/images/icons/assetGradingIcon.svg";
import lockIcon from "assets/images/icons/darkGreyLockIcon.svg";

import testIds from "testIds.json";

enum FILTER_PUBLISHED_VALUE {
    ALL = "",
    TRUE = "true",
    FALSE = "false",
}

const mapState = (state: StoreState) => ({
    user: state.userReducer.user,
    tenantDetails: state.tenantDetailsReducer.stack[state.tenantDetailsReducer.stack.length - 1],
    theme: state.themeReducer.theme,
    template: state.workflowTemplateReducer.workflowTemplateRestoreVersion,
});
const connector = connect(mapState, { setUser });

interface TableState {
    workflowTemplatesData: TemplateTableData[];
    count: number;
    cursor: string;
    scrollPosition?: number;
}

interface UserList {
    name: string;
    uuid: string;
}

interface PublishState {
    uuid: string;
    name: string;
    publishStatus: string;
    message: string;
    error: boolean;
    loading: boolean;
    setPublished?: React.Dispatch<SetStateAction<boolean>> | undefined;
    setMenuHidden?: React.Dispatch<SetStateAction<boolean>> | undefined;
}

export interface UpdateDialogDetails {
    visible: boolean;
    uuid: string;
    profile: string;
    editorVersion: string;
    name?: string;
}

const WorkflowTemplateTable = (
    props: ConnectedProps<typeof connector> & {
        isSubWorkflow: boolean;
        count: number;
        setCount: React.Dispatch<React.SetStateAction<number>>;
        search: string;
        filterState?: FilterState;
        setFilterState?: React.Dispatch<React.SetStateAction<FilterState>>;
        tableIdentity: RepositoryKey;
        onDelete: () => void;
        onUpdate: () => void;
    }
): JSX.Element => {
    {
        const { t } = useTranslation();
        const [loading, setLoading] = React.useState<boolean>(false);
        const [initialLoading, setInitialLoading] = React.useState<boolean>(false);
        const [requestFailureMessage, setRequestFailureMessage] = React.useState<string>("");
        const [workflowEditors, setWorkflowEditors] = React.useState<ManifestWorkflowEditor[]>([]);
        const [workflowEditorsLoaded, setWorkflowEditorsLoaded] = React.useState(false);
        const [userList, setUsersList] = React.useState<UserList[]>([]);

        const [tableState, setTableState] = React.useState<TableState>({
            workflowTemplatesData: [],
            count: 0,
            cursor: "",
            scrollPosition: 0,
        });
        const tableContainerRef = React.useRef<HTMLDivElement>(null);
        const [filterProductType, setFilterProductType] = React.useState("");
        const [filterPublishStatus, setFilterPublishStatus] = React.useState("");
        const [filterProduct, setFilterProduct] = React.useState("");
        const [filterCreator, setFilterCreator] = React.useState("");
        const [optionList, setOptionList] = React.useState<JSX.Element[] | undefined>(undefined);
        const [search, setSearchQuery] = React.useState("");
        const { current: abortControllers } = React.useRef<AbortController[]>([]);
        const [uuid, setUuid] = React.useState<string>();
        const [name, setName] = React.useState<string>();
        const [workflowProduct, setWorkflowProduct] = React.useState("");
        const [workflowVersion, setWorkflowVersion] = React.useState("");
        const [restoreVersionId, setRestoreVersionId] = React.useState<string | undefined>(undefined);

        const [editWorkflowVisible, setEditWorkflowVisible] = React.useState(false);
        const [updateDialogVisibilityDetails, setUpdateDialogVisibilityDetails] = React.useState<UpdateDialogDetails>({
            visible: false,
            uuid: "",
            profile: "",
            editorVersion: "",
        });

        const [publishState, setPublishState] = React.useState<PublishState>({
            uuid: "",
            name: "",
            publishStatus: "",
            message: "",
            error: false,
            loading: false,
        });
        const [uploadTemplateDialogVisible, setUploadTemplateDialogVisible] = React.useState(false);
        const [uploadWorkflowFileList, setUploadWorkflowFileList] = React.useState<FileList | undefined>(undefined);
        const [uploadTemplateModalTitle, setUploadTemplateModalTitle] = React.useState<string>(
            t("workflowTemplatesManager.ImportWorkflowTemplateDialog.title")
        );
        const [uploadTemplateModalIcon, setUploadTemplateModalIcon] = React.useState<JSX.Element | undefined>(
            undefined
        );

        const [selectedRow, setSelectedRow] = React.useState<TemplateTableData | undefined>(undefined);
        const [isSidePanelOpen, setIsSidePanelOpen] = React.useState<boolean>(false);
        const workflowEditorV3Flag = Boolean(useFeature(FLAG_WORKFLOW_EDITOR_V3));
        const [workflowEditorV3Supported, setWorkflowEditorV3Supported] = React.useState<
            WorkflowEditorV3SupportedEntry[]
        >([]);
        const [workflowEditorV3SupportedLoaded, setWorkflowEditorV3SupportedLoaded] = React.useState(false);
        const [showEditorV3, setShowEditorV3] = React.useState(false);
        const [workflowUuidForV3, setWorkflowUuidForV3] = React.useState<string | undefined>(undefined);
        const [editorV3VersionId, setEditorV3VersionId] = React.useState<string | undefined>(undefined);

        const theme = useTheme();
        const entityType = props.isSubWorkflow ? WorkflowEntityType.SUB_WORKFLOW : WorkflowEntityType.TEMPLATE;

        const handleStartDateChange = (date: string) => {
            handleFilterChanges({ from: date });
        };
        const handleEndDateChange = (date: string) => {
            handleFilterChanges({ to: date });
        };
        const versionValue = filterProductType.includes("Erasure")
            ? filterProductType.replace("Erasure", "")
            : filterProductType.replace("Diagnostics", "");

        const handleFilterChanges = (update: Partial<FilterState>) => {
            if (props.setFilterState) {
                props.setFilterState((prevState) => ({
                    ...prevState,
                    ...update,
                }));
            }
        };
        const handlePublishedChanged = (
            uuid: string,
            name: string,
            publishStatus: string,
            setPublished: React.Dispatch<React.SetStateAction<boolean>> | undefined,
            setMenuHidden: React.Dispatch<React.SetStateAction<boolean>> | undefined
        ) => {
            const abortController = new AbortController();
            abortControllers.push(abortController);
            setPublishState((prevState) => ({
                ...prevState,
                uuid: uuid,
                name: name,
                setPublished: setPublished,
                publishStatus: publishStatus,
                loading: true,
            }));
            const newStatus = PublishStatus.PUBLISHED === publishStatus;
            const message = newStatus
                ? t("workflowTemplatesManager.publish.publishSuccessMessage", { name: name })
                : t("workflowTemplatesManager.publish.unpublishSuccessMessage", { name: name });
            if (setMenuHidden) {
                setMenuHidden(true);
            }
            templateService
                .publish(uuid, newStatus, abortController)
                .then(() => {
                    if (setPublished) {
                        setPublished(newStatus);
                    }
                    setPublishState((prevState) => ({
                        ...prevState,
                        error: false,
                    }));
                    toast(
                        <SuccessIcon
                            successClass={layout.customToastSuccessIcon}
                            color={theme.contentBackgroundColor}
                            text={message}
                        />,
                        {
                            closeButton: (closeToastProps) => (
                                <CustomToastCloseButton
                                    closeToast={{ ...closeToastProps }}
                                    color={theme.iconFillColor}
                                />
                            ),
                            className: layout.customToastSuccessMessage,
                        }
                    );
                })
                .catch(() => {
                    if (setPublished) {
                        setPublished(!newStatus);
                    }
                    setPublishState((prevState) => ({
                        ...prevState,
                        message: t("workflowTemplatesManager.publish.publishingFailed"),
                        error: true,
                    }));
                })
                .finally(() => {
                    setPublishState((prevState) => ({
                        ...prevState,
                        loading: false,
                    }));
                    if (setMenuHidden) {
                        setMenuHidden(false);
                    }
                });
        };
        const hasPublishAuthority = userSessionService.userHasAllAuthorities([AUTH_WORKFLOW_TEMPLATE_PUBLISH]);

        const openEditorV3 = (uuid: string, versionId?: string) => {
            setWorkflowUuidForV3(uuid);
            setEditorV3VersionId(versionId);
            setShowEditorV3(true);
        };

        const closeEditorV3 = () => {
            setShowEditorV3(false);
            setEditorV3VersionId(undefined);
        };

        const showManageDialog = (uuid: string, version: string, product: string, name: string) => {
            const decision = determineEditorToOpen(
                product,
                version,
                workflowEditorV3Supported,
                workflowEditors,
                workflowEditorV3Flag,
                determineEditorGenerationByProfileAndVersion
            );

            if (decision.openV3) {
                openEditorV3(uuid);
                return;
            }

            setUuid(uuid);
            setWorkflowVersion(version);
            setWorkflowProduct(product);
            setName(name);
            setEditWorkflowVisible(true);
        };

        const handleRestoreVersion = (
            uuid: string,
            editorVersion: string,
            profile: string,
            restoreVersionId: string
        ) => {
            setUuid(uuid);
            setWorkflowVersion(editorVersion);
            setWorkflowProduct(profile);
            setRestoreVersionId(restoreVersionId);
            setEditWorkflowVisible(true);
        };

        const handleVersionRestoreFromV3 = (params: VersionRestoreParams) => {
            closeEditorV3();
            handleRestoreVersion(
                params.workflowUuid,
                params.editorVersion,
                params.product.toLowerCase(),
                params.versionId
            );
        };

        const handleUpdateOnNameClick = (
            generation: string | null,
            version: string,
            uuid: string,
            profile: string,
            name: string
        ) => {
            if (isEditorV3Supported(workflowEditorV3Supported, profile, version)) {
                showManageDialog(uuid, version, profile, name);
                return;
            }

            const editorGeneration = generation;
            const editorVersion = deduceEditorVersion(version);

            if (
                editorGeneration === "V2" &&
                ![
                    BDE_WORKFLOW_EDITOR_VERSION_7_12_0,
                    BDE_WORKFLOW_EDITOR_VERSION_7_13_0,
                    BMDE_WORKFLOW_EDITOR_VERSION_5_4_0,
                    BMDE_WORKFLOW_EDITOR_VERSION_5_5_0,
                ].includes(editorVersion)
            ) {
                showManageDialog(uuid, version, profile, name);
            } else {
                setUpdateDialogVisibilityDetails({
                    visible: true,
                    uuid: uuid,
                    profile: profile,
                    editorVersion: version,
                    name: name,
                });
            }
        };

        const latestEditorVersions =
            workflowEditors.length > 0 ? deduceLatestEditorVersions(workflowEditors) : undefined;

        const columns: Array<Column<TemplateTableData>> = props.isSubWorkflow
            ? [
                  {
                      Header: () => <TextWithTooltip text={t("workflowTemplatesManager.subWorkflows.name")} key="1" />,
                      accessor: "name",
                      Cell: (cellInfo) => {
                          const isBmpSecured =
                              cellInfo.cell.row.original.secured && cellInfo.cell.row.original.profile === Profile.BMP;
                          const isSecuredWithoutAuthority =
                              isBmpSecured &&
                              !userSessionService.userHasAllAuthorities([AUTH_WORKFLOW_TEMPLATE_SECURED]);

                          return (
                              <>
                                  {userSessionService.userHasAnyAuthority([
                                      AUTH_WORKFLOW_TEMPLATE_MANAGEMENT_VIEW,
                                      AUTH_WORKFLOW_TEMPLATE_DELETE,
                                  ]) &&
                                      !isSecuredWithoutAuthority && (
                                          <KebabMenu preventEventPropagation={true}>
                                              <WorkflowTemplateKebabMenu
                                                  uuid={cellInfo.cell.row.original.uuid}
                                                  name={cellInfo.cell.row.original.name}
                                                  connectedWorkflows={cellInfo.row.original.connectedWorkflows}
                                                  onDelete={props.onDelete}
                                                  onUpdate={props.onUpdate}
                                                  isSubWorkflow={props.isSubWorkflow}
                                                  version={cellInfo.cell.row.original.editorVersion}
                                                  product={cellInfo.cell.row.original.profile}
                                                  updateVisible={isNewerEditorAvailable(
                                                      cellInfo.cell.row.original.profile,
                                                      cellInfo.cell.row.original.editorGeneration,
                                                      cellInfo.cell.row.original.editorVersion,
                                                      latestEditorVersions,
                                                      workflowEditorV3Supported
                                                  )}
                                                  setUpdateDialogVisibility={setUpdateDialogVisibilityDetails}
                                                  setIsSidePanelOpen={setIsSidePanelOpen}
                                              />
                                          </KebabMenu>
                                      )}
                                  {isSecuredWithoutAuthority ? (
                                      <div className={workflowStyle.nameContainer}>
                                          {isBmpSecured && (
                                              <Tooltip content={t("WorkflowsTable.setWorkflowSecured.tooltip")}>
                                                  <img
                                                      src={lockIcon}
                                                      alt="secured"
                                                      style={{ marginRight: 4, width: 14, height: 14 }}
                                                  />
                                              </Tooltip>
                                          )}
                                          {cellInfo.cell.row.original.assetGrading && (
                                              <Tooltip content={t("workflowTemplate.assetGrading")}>
                                                  <img
                                                      src={assetGradingIcon}
                                                      alt="asset grading"
                                                      style={{ marginRight: 4, width: 14, height: 14 }}
                                                  />
                                              </Tooltip>
                                          )}
                                          <TextWithTooltip text={cellInfo.value} />
                                      </div>
                                  ) : (
                                      <button
                                          className={style.templateNameCell}
                                          onClick={(e) => {
                                              e.stopPropagation();
                                              if (
                                                  userSessionService.userHasAllAuthorities([
                                                      AUTH_WORKFLOW_TEMPLATE_EDIT,
                                                  ])
                                              ) {
                                                  handleUpdateOnNameClick(
                                                      cellInfo.cell.row.original.editorGeneration,
                                                      cellInfo.cell.row.original.editorVersion,
                                                      cellInfo.cell.row.original.uuid,
                                                      cellInfo.cell.row.original.profile,
                                                      cellInfo.cell.row.original.name
                                                  );
                                              }
                                          }}
                                      >
                                          {cellInfo.cell.row.original.secured &&
                                              cellInfo.cell.row.original.profile === Profile.BMP && (
                                                  <Tooltip content={t("Common.secured")}>
                                                      <img
                                                          src={lockIcon}
                                                          alt="secured"
                                                          style={{ marginRight: 4, width: 14, height: 14 }}
                                                      />
                                                  </Tooltip>
                                              )}
                                          {cellInfo.cell.row.original.assetGrading && (
                                              <Tooltip content={t("workflowTemplate.assetGrading")}>
                                                  <img
                                                      src={assetGradingIcon}
                                                      alt="asset grading"
                                                      style={{ marginRight: 4, width: 14, height: 14 }}
                                                  />
                                              </Tooltip>
                                          )}
                                          <TextWithTooltip text={cellInfo.value} />
                                      </button>
                                  )}
                              </>
                          );
                      },
                      width: deriveColumnWidth(35, tableContainerRef),
                  },
                  {
                      Header: () => (
                          <TextWithTooltip text={t("workflowTemplatesManager.subWorkflows.product")} key="2" />
                      ),
                      accessor: "profile",
                      Cell: ({ cell: { row } }) => (
                          <ProductNameColumn
                              profile={row.original.profile.toLowerCase()}
                              version={row.original.editorVersion}
                              generation={row.original.editorGeneration}
                              t={t}
                          />
                      ),
                      width: deriveColumnWidth(35, tableContainerRef),
                  },
                  {
                      Header: () => (
                          <TextWithTooltip text={t("workflowTemplatesManager.subWorkflows.connections")} key="3" />
                      ),
                      accessor: "connectedWorkflows",
                      Cell: ({ cell: { value } }) => {
                          return value.totalConnectedWorkflows ? (
                              <ConnectedWorkflowsList connectedWorkflows={value} isSubWorkflow={true} />
                          ) : (
                              <div className={style.alignRight}>-</div>
                          );
                      },
                      width: deriveColumnWidth(35, tableContainerRef),
                  },
                  {
                      Header: () => <TextWithTooltip text={t("Common.creationDate")} key="4" />,
                      accessor: "created",
                      Cell: ({ cell: { value } }) => <DateCell tooltip={true} value={value} />,
                      width: deriveColumnWidth(35, tableContainerRef),
                  },
                  {
                      Header: () => (
                          <TextWithTooltip text={t("workflowTemplatesManager.subWorkflows.creator")} key="5" />
                      ),
                      accessor: "userName",
                      Cell: (cellInfo) => <TextWithTooltip text={cellInfo.value} />,
                      width: deriveColumnWidth(35, tableContainerRef),
                  },
              ]
            : [
                  {
                      Header: () => <TextWithTooltip text={t("workflowTemplatesManager.template.name")} key="1" />,
                      accessor: "name",
                      Cell: (cellInfo) => {
                          const isBmpSecured =
                              cellInfo.cell.row.original.secured && cellInfo.cell.row.original.profile === Profile.BMP;
                          const isSecuredWithoutAuthority =
                              isBmpSecured &&
                              !userSessionService.userHasAllAuthorities([AUTH_WORKFLOW_TEMPLATE_SECURED]);

                          return (
                              <>
                                  {userSessionService.userHasAnyAuthority([
                                      AUTH_WORKFLOW_TEMPLATE_MANAGEMENT_VIEW,
                                      AUTH_WORKFLOW_TEMPLATE_DELETE,
                                  ]) &&
                                      !isSecuredWithoutAuthority && (
                                          <KebabMenu preventEventPropagation={true}>
                                              <WorkflowTemplateKebabMenu
                                                  uuid={cellInfo.cell.row.original.uuid}
                                                  name={cellInfo.cell.row.original.name}
                                                  connectedWorkflows={cellInfo.row.original.connectedWorkflows}
                                                  onDelete={props.onDelete}
                                                  onUpdate={props.onUpdate}
                                                  isSubWorkflow={props.isSubWorkflow}
                                                  version={cellInfo.cell.row.original.editorVersion}
                                                  product={cellInfo.cell.row.original.profile}
                                                  updateVisible={isNewerEditorAvailable(
                                                      cellInfo.cell.row.original.profile,
                                                      cellInfo.cell.row.original.editorGeneration,
                                                      cellInfo.cell.row.original.editorVersion,
                                                      latestEditorVersions,
                                                      workflowEditorV3Supported
                                                  )}
                                                  setUpdateDialogVisibility={setUpdateDialogVisibilityDetails}
                                                  setIsSidePanelOpen={setIsSidePanelOpen}
                                              />
                                          </KebabMenu>
                                      )}

                                  {isSecuredWithoutAuthority ? (
                                      <div className={workflowStyle.nameContainer}>
                                          {isBmpSecured && (
                                              <Tooltip content={t("WorkflowsTable.setWorkflowSecured.tooltip")}>
                                                  <img
                                                      src={lockIcon}
                                                      alt="secured"
                                                      style={{ marginRight: 4, width: 14, height: 14 }}
                                                  />
                                              </Tooltip>
                                          )}
                                          {cellInfo.cell.row.original.schedule !== undefined && (
                                              <Tooltip
                                                  content={buildScheduleSummary(
                                                      {
                                                          launchSchedule: cellInfo.cell.row.original.schedule.frequency,
                                                          everyHours:
                                                              cellInfo.cell.row.original.schedule.recurrence.every || 1,
                                                          launchTimeHours:
                                                              cellInfo.cell.row.original.schedule.recurrence.time?.split(
                                                                  ":"
                                                              )[0] || "00",
                                                          launchTimeMinutes:
                                                              cellInfo.cell.row.original.schedule.recurrence.time?.split(
                                                                  ":"
                                                              )[1] || "00",
                                                          timezone: cellInfo.cell.row.original.schedule.timezone,
                                                          daysOfWeek: (cellInfo.cell.row.original.schedule.recurrence
                                                              .days || []) as DayOfWeek[],
                                                          dayOfMonth:
                                                              cellInfo.cell.row.original.schedule.recurrence.day || 1,
                                                      },
                                                      t
                                                  )}
                                              >
                                                  <div className={style.scheduledIconContainer}>
                                                      <ScheduledIcon color={theme.textGreyColor} size={14} />
                                                  </div>
                                              </Tooltip>
                                          )}
                                          {cellInfo.cell.row.original.assetGrading && (
                                              <Tooltip content={t("workflowTemplate.assetGrading")}>
                                                  <img
                                                      src={assetGradingIcon}
                                                      alt="asset grading"
                                                      style={{ marginRight: 4, width: 14, height: 14 }}
                                                  />
                                              </Tooltip>
                                          )}
                                          <TextWithTooltip text={cellInfo.value} />
                                      </div>
                                  ) : (
                                      <button
                                          className={style.templateNameCell}
                                          onClick={(e) => {
                                              e.stopPropagation();
                                              if (
                                                  userSessionService.userHasAllAuthorities([
                                                      AUTH_WORKFLOW_TEMPLATE_EDIT,
                                                  ])
                                              ) {
                                                  handleUpdateOnNameClick(
                                                      cellInfo.cell.row.original.editorGeneration,
                                                      cellInfo.cell.row.original.editorVersion,
                                                      cellInfo.cell.row.original.uuid,
                                                      cellInfo.cell.row.original.profile,
                                                      cellInfo.cell.row.original.name
                                                  );
                                              }
                                          }}
                                      >
                                          {cellInfo.cell.row.original.secured &&
                                              cellInfo.cell.row.original.profile === Profile.BMP && (
                                                  <Tooltip content={t("Common.secured")}>
                                                      <img
                                                          src={lockIcon}
                                                          alt="secured"
                                                          style={{ marginRight: 4, width: 14, height: 14 }}
                                                      />
                                                  </Tooltip>
                                              )}
                                          {cellInfo.cell.row.original.assetGrading && (
                                              <Tooltip content={t("workflowTemplate.assetGrading")}>
                                                  <img
                                                      src={assetGradingIcon}
                                                      alt="asset grading"
                                                      style={{ marginRight: 4, width: 14, height: 14 }}
                                                  />
                                              </Tooltip>
                                          )}
                                          {cellInfo.cell.row.original.schedule !== undefined && (
                                              <Tooltip
                                                  content={buildScheduleSummary(
                                                      {
                                                          launchSchedule: cellInfo.cell.row.original.schedule.frequency,
                                                          everyHours:
                                                              cellInfo.cell.row.original.schedule.recurrence.every || 1,
                                                          launchTimeHours:
                                                              cellInfo.cell.row.original.schedule.recurrence.time?.split(
                                                                  ":"
                                                              )[0] || "00",
                                                          launchTimeMinutes:
                                                              cellInfo.cell.row.original.schedule.recurrence.time?.split(
                                                                  ":"
                                                              )[1] || "00",
                                                          timezone: cellInfo.cell.row.original.schedule.timezone,
                                                          daysOfWeek: (cellInfo.cell.row.original.schedule.recurrence
                                                              .days || []) as DayOfWeek[],
                                                          dayOfMonth:
                                                              cellInfo.cell.row.original.schedule.recurrence.day || 1,
                                                      },
                                                      t
                                                  )}
                                              >
                                                  <div className={style.scheduledIconContainer}>
                                                      <ScheduledIcon color={theme.textGreyColor} size={14} />
                                                  </div>
                                              </Tooltip>
                                          )}
                                          <TextWithTooltip text={cellInfo.value} />
                                      </button>
                                  )}
                              </>
                          );
                      },
                      width: deriveColumnWidth(20, tableContainerRef),
                  },
                  {
                      Header: () => <TextWithTooltip text={t("workflowTemplatesManager.template.product")} key="4" />,
                      accessor: "profile",
                      Cell: ({ cell: { row } }) => (
                          <ProductNameColumn
                              profile={row.original.profile.toLowerCase()}
                              version={row.original.editorVersion}
                              generation={row.original.editorGeneration}
                              t={t}
                          />
                      ),
                      width: deriveColumnWidth(32, tableContainerRef),
                  },
                  {
                      Header: () => <TextWithTooltip text={t("Common.status")} key="3" />,
                      accessor: "published",
                      Cell: (cellInfo) => {
                          return (
                              <div onClick={(e) => e.stopPropagation()}>
                                  <PublishBadge
                                      published={cellInfo.value}
                                      uuid={cellInfo.row.original.uuid}
                                      name={cellInfo.row.original.name}
                                      handlePublishedChanged={handlePublishedChanged}
                                      hasAuthority={hasPublishAuthority}
                                  />
                              </div>
                          );
                      },
                      width: deriveColumnWidth(20, tableContainerRef),
                  },
                  {
                      Header: () => (
                          <TextWithTooltip text={t("workflowTemplatesManager.template.usageCount")} key="8" />
                      ),
                      accessor: "usageCount",
                      Cell: (cellInfo) => (
                          <div className={style.alignRight}>
                              <TextWithTooltip text={cellInfo.value} />
                          </div>
                      ),
                      width: deriveColumnWidth(15, tableContainerRef),
                  },
                  {
                      Header: () => <TextWithTooltip text={t("workflowTemplatesManager.subWorkflows.title")} key="2" />,
                      accessor: "connectedWorkflows",
                      Cell: ({ cell: { value } }) => {
                          return value.totalConnectedWorkflows > 0 ? (
                              <ConnectedWorkflowsList connectedWorkflows={value} isSubWorkflow={false} />
                          ) : (
                              <div className={style.alignRight}>-</div>
                          );
                      },
                      width: deriveColumnWidth(20, tableContainerRef),
                  },

                  {
                      Header: () => <TextWithTooltip text={t("workflowTemplatesManager.template.createdBy")} key="5" />,
                      accessor: "userName",
                      Cell: (cellInfo) => <TextWithTooltip text={cellInfo.value} />,
                      width: deriveColumnWidth(23, tableContainerRef),
                  },
                  {
                      Header: () => <TextWithTooltip text={t("Common.creationDate")} key="6" />,
                      accessor: "created",
                      Cell: ({ cell: { value } }) => <DateCell tooltip={true} value={value} />,
                      width: deriveColumnWidth(22, tableContainerRef),
                  },

                  {
                      Header: () => (
                          <TextWithTooltip text={t("workflowTemplatesManager.template.lastModified")} key="7" />
                      ),
                      accessor: "modified",
                      Cell: ({ cell: { value } }) => <DateCell tooltip={true} value={value} />,
                      width: deriveColumnWidth(22, tableContainerRef),
                  },
              ];

        const fetchData = (initialLoading: boolean, abortController: AbortController) => {
            setLoading(true);
            setInitialLoading(initialLoading);
            templateService
                .fetchWorkflowTemplates(
                    search,
                    initialLoading ? "" : tableState.cursor,
                    filterProduct.toUpperCase(),
                    "",
                    [],
                    [],
                    [],
                    FILTER_PUBLISHED_VALUE.ALL === filterPublishStatus
                        ? undefined
                        : FILTER_PUBLISHED_VALUE.TRUE === filterPublishStatus,
                    versionValue.trimEnd(),
                    props.filterState?.from,
                    props.filterState?.to,
                    props.isSubWorkflow,
                    filterCreator,
                    false,
                    abortController
                )
                .then((data) => {
                    setTableState((prevState) => ({
                        ...prevState,
                        scrollPosition: prevState.workflowTemplatesData.length - 1,
                        workflowTemplatesData: prevState.workflowTemplatesData.concat(data.workflowTemplatesTableData),
                        count: data.count,
                        cursor: data.cursor,
                    }));
                    setLoading(false);
                    setRequestFailureMessage("");
                })
                .catch(() => {
                    if (!abortController.signal.aborted) {
                        setRequestFailureMessage(
                            t(
                                props.isSubWorkflow
                                    ? "workflowTemplatesManager.template.requestFailedSubWorkflow"
                                    : "workflowTemplatesManager.template.requestFailed"
                            )
                        );
                    }
                })
                .finally(() => {
                    if (!abortController.signal.aborted) {
                        setLoading(false);
                        setInitialLoading(false);
                    }
                });
        };

        React.useEffect(() => {
            const abortController = new AbortController();
            trackFetchLoaded(
                workflowService.fetchWorkflowEditors(Profile.ALL, abortController),
                abortController,
                setWorkflowEditors,
                setWorkflowEditorsLoaded,
                "Failed to fetch workflow editor manifest; the Update option will not be shown for outdated workflows."
            );
            const v3Controller = new AbortController();
            if (workflowEditorV3Flag) {
                trackFetchLoaded(
                    workflowService.fetchWorkflowEditorV3Supported(v3Controller),
                    v3Controller,
                    setWorkflowEditorV3Supported,
                    setWorkflowEditorV3SupportedLoaded,
                    "Failed to fetch V3 supported versions; V3 editor options will not be shown."
                );
            }
            return () => {
                abortController.abort();
                v3Controller.abort();
            };
        }, [workflowEditorV3Flag]);

        React.useEffect(() => {
            const abortController = new AbortController();
            abortControllers.push(abortController);
            userService.fetchAllUsers(abortController, "true").then((data) => {
                setUsersList(
                    data.userTableData
                        .map((each) => {
                            return { name: each.name ?? each.email, uuid: each.uuid };
                        })
                        .sort((a, b) => a.name.localeCompare(b.name))
                );
            });
            return () => {
                abortControllers.forEach((abortController) => abortController.abort());
            };
        }, []);

        React.useEffect(() => {
            const abortController = new AbortController();
            setUploadTemplateModalTitle(t("workflowTemplatesManager.ImportWorkflowTemplateDialog.title"));
            setTableState({ workflowTemplatesData: [], count: 0, cursor: "", scrollPosition: 0 });
            fetchData(true, abortController);
            return () => {
                abortController.abort();
            };
        }, [
            props.count,
            search,
            filterProduct.toUpperCase(),
            filterProductType,
            filterPublishStatus,
            props.filterState?.from,
            props.filterState?.to,
            filterCreator,
        ]);

        React.useEffect(() => {
            let sortedEditors = workflowEditors
                .filter((each) => each.editorGeneration === "V2")
                .filter((editor) => {
                    if (
                        [Profile.BDE.toString(), Profile.LUN.toString(), Profile.BMP.toString()].includes(filterProduct)
                    ) {
                        return filterProduct === editor.profile;
                    }
                    return Profile.BMDE === editor.profile || Profile.BMDE_DIAGNOSTIC === editor.profile;
                })
                .sort(compareEditorVersionDtos);

            //  Remove the BDE new generation editor old versions from list which
            //  do not support workflow templates.
            if (filterProduct === Profile.BDE) {
                sortedEditors = sortedEditors.filter(
                    (each) =>
                        !each.version.includes(BDE_WORKFLOW_EDITOR_VERSION_7_12_0) &&
                        !each.version.includes(BDE_WORKFLOW_EDITOR_VERSION_7_13_0)
                );
            }

            // Remove the BMDE new generation editor old versions from list which
            // do not support workflow templates.
            if (filterProduct === Profile.BMDE || filterProduct === Profile.BMDE_DIAGNOSTIC) {
                sortedEditors = sortedEditors.filter(
                    (each) =>
                        !each.version.includes(BMDE_WORKFLOW_EDITOR_VERSION_5_4_0) &&
                        !each.version.includes(BMDE_WORKFLOW_EDITOR_VERSION_5_5_0)
                );
            }

            const removeDuplicateVersion = sortedEditors.filter(
                (obj, index, self) => index === self.findIndex((each) => each.version === obj.version)
            );

            const allVersions = mergeAndSortEditorVersions(
                removeDuplicateVersion.map((entry) => entry.version),
                workflowEditorV3Supported,
                filterProduct
            );
            setOptionList(allVersions.map((version) => <option key={version}>{version}</option>));
        }, [filterProduct, workflowEditorV3Supported]);
        let dataCount = null;
        if (tableState.workflowTemplatesData.length > 0) {
            dataCount = t("Common.recordsCount", { dataCount: tableState.workflowTemplatesData.length });
        }

        const resetPublishState = () => {
            setPublishState(() => ({
                uuid: "",
                name: "",
                publishStatus: "",
                message: "",
                error: false,
                loading: false,
            }));
        };

        const showUploadTemplateDialog = (fileList: FileList) => {
            setUploadWorkflowFileList(fileList);
            setUploadTemplateDialogVisible(true);
        };

        const showUploadSubWorkflowDialog = (fileList: FileList) => {
            setUploadTemplateModalTitle(t("workflowTemplatesManager.ImportSubWorkflowTemplateDialog.title"));
            setUploadWorkflowFileList(fileList);
            setUploadTemplateDialogVisible(true);
        };

        const hideUploadTemplateDialog = () => {
            setUploadTemplateModalTitle(t("workflowTemplatesManager.ImportWorkflowTemplateDialog.title"));
            setUploadTemplateModalIcon(undefined);
            setUploadTemplateDialogVisible(false);
            setUploadWorkflowFileList(undefined);
            props.setCount((prev) => ++prev);
        };

        const handleFileDrop = (files: FileList) => {
            if (files.length === 0) {
                return;
            }
            usageStatisticsService.sendEvent({
                category: Category.WORKFLOW,
                action: Action.IMPORT_WORKFLOW,
            });
            if (props.isSubWorkflow) {
                showUploadSubWorkflowDialog(files);
            } else {
                showUploadTemplateDialog(files);
            }
        };

        const deduceEditorVersion = (editorVersion: string) => {
            const lowercaseEditorVersion = editorVersion.toLowerCase();
            if (lowercaseEditorVersion.includes("dev")) {
                return editorVersion.replace("dev", "");
            } else if (lowercaseEditorVersion.includes("rc")) {
                return lowercaseEditorVersion.split("rc")[0];
            } else if (lowercaseEditorVersion.includes("a")) {
                return lowercaseEditorVersion.split("a")[0];
            } else {
                return lowercaseEditorVersion;
            }
        };

        const closeSidePanel = () => {
            setIsSidePanelOpen(false);
            setSelectedRow(undefined);
        };

        React.useEffect(() => {
            const details = getWorkflowTemplateRestoreVersionDetails();
            if (details) {
                setUuid(details.workflowUuid);
                setWorkflowVersion(details.editorVersion);
                setWorkflowProduct(details.profile.toLowerCase());
                setRestoreVersionId(details.versionId);

                setEditWorkflowVisible(true);
                clearWorkflowTemplateRestoreVersionDetails();
            }
        }, []);

        const tableReady =
            !initialLoading && workflowEditorsLoaded && (!workflowEditorV3Flag || workflowEditorV3SupportedLoaded);

        return (
            <>
                <div className={layoutStyle.aboveTable}>
                    <div className={layoutStyle.recordCount}>{dataCount}</div>
                    <div className={workflowStyle.filter}>
                        <span className={workflowStyle.label}>{t("WorkflowsTable.filter.filterBy")}</span>
                        <div className={formStyle.search}>
                            <select
                                id={"product"}
                                value={filterProduct}
                                onChange={(event) => {
                                    setFilterProduct(event.target.value);
                                    setFilterProductType("");
                                }}
                                className={classNames(formStyle.select, formStyle.fixedWidthInput)}
                                data-testid={testIds.workArea.workflowTemplatesManager.primaryView.filter.productSelect}
                            >
                                <option key={"empty"} value={""}>
                                    {t("workflowTemplatesManager.filter.allProducts")}
                                </option>
                                {Object.values(Profile)
                                    .filter((profile) => Profile.ALL !== profile)
                                    .filter((profile) =>
                                        props.tenantDetails?.featureLicenses.includes(
                                            profileToFeatureLicenseMap.get(profile) as FeatureLicenseType
                                        )
                                    )
                                    .map((each, index) => (
                                        <option key={index} value={each}>
                                            {profileToNameMap.get(each)}
                                        </option>
                                    ))}
                            </select>
                        </div>
                        <div className={formStyle.search}>
                            <select
                                name="version"
                                value={filterProductType}
                                onChange={(e) => setFilterProductType(e.target.value)}
                                className={classNames(formStyle.select, workflowStyle.fixedWidthVersion)}
                                disabled={filterProduct === ""}
                                data-testid={testIds.workArea.workflowTemplatesManager.primaryView.filter.versionSelect}
                            >
                                <option key={"value"} value={""}>
                                    {t("workflowTemplatesManager.filter.allVersions")}
                                </option>
                                {optionList}
                            </select>
                        </div>
                        {!props.isSubWorkflow && (
                            <div className={formStyle.search}>
                                <select
                                    name="published"
                                    value={filterPublishStatus}
                                    onChange={(e) => setFilterPublishStatus(e.target.value)}
                                    className={classNames(formStyle.select, workflowStyle.fixedWidthVersion)}
                                    data-testid={
                                        testIds.workArea.workflowTemplatesManager.primaryView.filter.statusSelect
                                    }
                                >
                                    <option key={"value"} value={""}>
                                        {t("workflowTemplatesManager.filter.allStatuses")}
                                    </option>
                                    {Object.values(PublishStatus).map((each, index) => {
                                        return (
                                            <option
                                                key={index}
                                                value={
                                                    PublishStatus.PUBLISHED === each
                                                        ? FILTER_PUBLISHED_VALUE.TRUE
                                                        : FILTER_PUBLISHED_VALUE.FALSE
                                                }
                                            >
                                                {each}
                                            </option>
                                        );
                                    })}
                                </select>
                            </div>
                        )}
                        <div className={formStyle.search}>
                            {}
                            <select
                                name="creator"
                                value={filterCreator}
                                onChange={(e) => setFilterCreator(e.target.value)}
                                className={classNames(formStyle.select, workflowStyle.fixedWidthVersion)}
                                data-testid={testIds.workArea.workflowTemplatesManager.primaryView.filter.creatorSelect}
                            >
                                <option key={"value"} value={""}>
                                    {t("workflowTemplatesManager.filter.allCreators")}
                                </option>
                                {Object.values(userList).map((each, index) => {
                                    return (
                                        <option key={index} value={each.uuid}>
                                            {each.name}
                                        </option>
                                    );
                                })}
                            </select>
                        </div>
                        <div>
                            <WorkflowEventDatePicker from={handleStartDateChange} to={handleEndDateChange} />
                        </div>
                        <div className={formStyle.search}>
                            <SearchView setSearch={setSearchQuery} searchInProgress={false} />
                        </div>
                    </div>
                </div>
                <div className={layoutStyle.tableWrapper}>
                    <FileDrop onDrop={handleFileDrop}>
                        <div
                            className={classNames(
                                !tableReady && tableState.workflowTemplatesData.length < 1
                                    ? workflowStyle.loadingIndicator
                                    : "",
                                workflowStyle.tableStyle
                            )}
                        >
                            {tableState.workflowTemplatesData.length > 0 || initialLoading ? (
                                <Table
                                    key={tableReady ? "loaded" : "loading"}
                                    tableIdentity={props.tableIdentity}
                                    data={tableState.workflowTemplatesData}
                                    columns={columns}
                                    loaded={tableReady}
                                    loading={loading}
                                    failureMessage={requestFailureMessage}
                                    tooltips={true}
                                    scrollTo={tableState.scrollPosition}
                                    emptyMessage={t("workflowTemplatesManager.template.emptyStateMessage")}
                                    data-testId={testIds.workArea.workflowTemplatesManager.primaryView.table}
                                    noAction={true}
                                    showSidePanel={true}
                                    setIsSidePanelOpen={setIsSidePanelOpen}
                                    setSelectedRow={setSelectedRow}
                                    isSidePanelOpen={isSidePanelOpen}
                                />
                            ) : (
                                <div className={style.errorMessage}>
                                    <EmptyStateIcon />
                                    <div className={style.errorMessageText}>
                                        <TextBlock disableBottomSpacing={false}>
                                            {requestFailureMessage !== undefined && requestFailureMessage != ""
                                                ? t(
                                                      props.isSubWorkflow
                                                          ? "workflowTemplatesManager.template.requestFailedSubWorkflow"
                                                          : "workflowTemplatesManager.template.requestFailed"
                                                  )
                                                : t(
                                                      props.isSubWorkflow
                                                          ? "workflowTemplatesManager.template.emptyStateMessageSubWorkflow"
                                                          : "workflowTemplatesManager.template.emptyStateMessage"
                                                  )}
                                        </TextBlock>
                                    </div>
                                </div>
                            )}
                        </div>
                        <SidePanel
                            onClose={closeSidePanel}
                            isSidePanelOpen={isSidePanelOpen}
                            selectedRow={selectedRow}
                            type={props.isSubWorkflow ? WorkflowEntityType.SUB_WORKFLOW : WorkflowEntityType.TEMPLATE}
                            testId={testIds.workArea.workflowTemplates.sidePanel.itself}
                            isSubWorkflow={props.isSubWorkflow}
                            onRestoreVersion={handleRestoreVersion}
                            onOpenWithV3={openEditorV3}
                            workflowEditorV3Supported={workflowEditorV3Supported}
                            onRefreshTable={props.onUpdate}
                            onCloseSidePanel={closeSidePanel}
                        />
                    </FileDrop>
                </div>
                {tableState.cursor != null &&
                    tableState.workflowTemplatesData.length != 0 &&
                    requestFailureMessage === "" &&
                    (loading ? (
                        <LoadingIndicator small={true} withTable />
                    ) : (
                        <button
                            className={buttonStyle.loadMoreButtonWithoutIcon}
                            onClick={() => {
                                fetchData(false, new AbortController());
                                usageStatisticsService.sendEvent({
                                    label: Label.WORKFLOW_TEMPLATES,
                                    action: Action.LOAD_MORE,
                                    category: Category.WORKFLOW_TEMPLATES,
                                });
                            }}
                            data-testid={testIds.common.primaryView.table.loadMoreButton}
                        >
                            {t("Common.loadMore")}
                        </button>
                    ))}
                <AlertDialog
                    isOpen={publishState.error}
                    hideModal={() => {
                        resetPublishState();
                    }}
                    type={"ERROR"}
                    primaryButton={{
                        action: () => {
                            handlePublishedChanged(
                                publishState.uuid,
                                publishState.name,
                                publishState.publishStatus,
                                publishState.setPublished,
                                publishState.setMenuHidden
                            );
                        },
                        text:
                            publishState.publishStatus === PublishStatus.PUBLISHED
                                ? t("workflowTemplatesManager.publish.publishAgain")
                                : t("workflowTemplatesManager.publish.unpublishAgain"),
                    }}
                    closeButton={{
                        action: () => {
                            resetPublishState();
                        },
                        text: t("Common.cancel"),
                    }}
                    loading={publishState.loading}
                    headingText={t("workflowTemplatesManager.publish.publishingFailed")}
                />
                <Modal
                    isOpen={uploadTemplateDialogVisible}
                    hideModal={hideUploadTemplateDialog}
                    modalTitle={uploadTemplateModalTitle}
                    titleIcon={uploadTemplateModalIcon}
                >
                    <ImportWorkflowsDialog
                        entityType={entityType}
                        fileList={uploadWorkflowFileList != null ? Array.from(uploadWorkflowFileList) : undefined}
                        onClose={hideUploadTemplateDialog}
                        service={templateService}
                        setModalTitle={setUploadTemplateModalTitle}
                        setModalTitleIcon={setUploadTemplateModalIcon}
                    />
                </Modal>
                <ManageWorkflowTemplateWrapper
                    modalTitle={""}
                    modalVisibility={editWorkflowVisible}
                    onUpdate={props.onUpdate}
                    workflowService={workflowService}
                    onShowModal={() => setEditWorkflowVisible(false)}
                    isCreate={false}
                    isEdit={true}
                    entityType={entityType}
                    name={name}
                    version={workflowVersion}
                    product={workflowProduct}
                    uuid={uuid}
                    restoreVersionId={restoreVersionId}
                    onVersionRestore={handleVersionRestoreFromV3}
                />
                <ManageWorkflowTemplateWrapper
                    modalTitle={
                        props.isSubWorkflow
                            ? "workflowTemplatesManager.updateSubWorkflowDialog.updateSubWorkflow"
                            : "workflowTemplatesManager.updateTemplateDialog.updateTemplate"
                    }
                    modalVisibility={updateDialogVisibilityDetails.visible}
                    onUpdate={props.onUpdate}
                    workflowService={workflowService}
                    onShowModal={() =>
                        setUpdateDialogVisibilityDetails({
                            visible: false,
                            uuid: "",
                            profile: "",
                            editorVersion: "",
                            name: "",
                        })
                    }
                    isUpdate={true}
                    entityType={entityType}
                    uuid={updateDialogVisibilityDetails.uuid}
                    product={updateDialogVisibilityDetails.profile}
                    version={updateDialogVisibilityDetails.editorVersion}
                    name={updateDialogVisibilityDetails.name}
                    onVersionRestore={handleVersionRestoreFromV3}
                />
                <WorkflowEditorV3View
                    isOpen={showEditorV3}
                    onClose={closeEditorV3}
                    onVersionRestore={handleVersionRestoreFromV3}
                    initialTabs={
                        workflowUuidForV3
                            ? [{ workflowUuid: workflowUuidForV3, versionId: editorV3VersionId }]
                            : undefined
                    }
                    entityType={entityType}
                />
            </>
        );
    }
};

const PublishBadge = (props: {
    uuid: string;
    name: string;
    published: boolean;
    hasAuthority: boolean;
    handlePublishedChanged: (
        uuid: string,
        name: string,
        publishStatus: string,
        setPublished: React.Dispatch<React.SetStateAction<boolean>>,
        setMenuHidden: React.Dispatch<React.SetStateAction<boolean>>
    ) => void;
}): JSX.Element => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [published, setPublished] = React.useState(props.published);
    const [menuHidden, setMenuHidden] = React.useState(false);
    const selectedTranslationText = published
        ? t("workflowTemplatesManager.template.published")
        : t("workflowTemplatesManager.template.unpublished");
    if (!props.hasAuthority) {
        return (
            <div className={style.alignLeft}>
                <StatusBadge
                    values={[{ status: published ? Status.SUCCESS : Status.NEUTRAL, title: selectedTranslationText }]}
                />
            </div>
        );
    }
    const selectedIconIndex = published ? 0 : 1;
    const menuItems: JSX.Element[] = [
        <MenuItemButton
            key="1"
            tabIndex={0}
            onClick={(e) => {
                e.stopPropagation();
                // Prevent publishing again with the same value pointlessly.
                if (selectedIconIndex === 0) {
                    return;
                }
                setMenuHidden(true);
                usageStatisticsService.sendEvent({
                    label: Label.WORKFLOW_TEMPLATES,
                    action: Action.PUBLISH_TEMPLATE,
                    category: Category.WORKFLOW_TEMPLATES,
                });
                setPublished(true);
                props.handlePublishedChanged(
                    props.uuid,
                    props.name,
                    PublishStatus.PUBLISHED,
                    setPublished,
                    setMenuHidden
                );
            }}
            data-testid={testIds.workArea.workflowTemplatesManager.primaryView.table.publishMenu.publishButton}
        >
            {t("workflowTemplatesManager.template.published")}
            {published ? <Checkmark color={theme.iconFillColor} /> : ""}
        </MenuItemButton>,
        <MenuItemButton
            key="2"
            tabIndex={0}
            onClick={(e) => {
                e.stopPropagation();
                if (selectedIconIndex === 1) {
                    return;
                }
                setMenuHidden(true);
                usageStatisticsService.sendEvent({
                    label: Label.WORKFLOW_TEMPLATES,
                    action: Action.UNPUBLISH_TEMPLATE,
                    category: Category.WORKFLOW_TEMPLATES,
                });
                setPublished(false);
                props.handlePublishedChanged(
                    props.uuid,
                    props.name,
                    PublishStatus.UNPUBLISHED,
                    setPublished,
                    setMenuHidden
                );
            }}
            data-testid={testIds.workArea.workflowTemplatesManager.primaryView.table.publishMenu.unpublishButton}
        >
            {t("workflowTemplatesManager.template.unpublished")}
            {!published ? <Checkmark color={theme.iconFillColor} /> : ""}
        </MenuItemButton>,
    ];
    return (
        <div className={style.alignLeft}>
            <ToggleablePanel
                title={""}
                icon={<TextWithTooltip text={selectedTranslationText}></TextWithTooltip>}
                menuType={"HEADER"}
                testId={testIds.workArea.workflowTemplatesManager.primaryView.table.publishMenu.button}
                hideChevron={false}
                chevronColor={published ? theme.successBadgeTextColor : theme.neutralBadgeTextColor}
                buttonClass={classNames(style.publishBadge, {
                    [statusStyle.statusSuccessful]: published,
                    [statusStyle.statusNeutral]: !published,
                })}
            >
                <Menu
                    className={classNames(style.publishMenu)}
                    data-testid={testIds.workArea.workflowTemplatesManager.primaryView.table.publishMenu.itself}
                    hidden={menuHidden}
                >
                    <ul>
                        {menuItems.map((item, key) => {
                            return <li key={key}>{item}</li>;
                        })}
                    </ul>
                </Menu>
            </ToggleablePanel>
        </div>
    );
};
export default connector(WorkflowTemplateTable);
